import { useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCANNERS = [
    ["3shape", "3Shape (TRIOS)"],
    ["dscore", "DS Core (Primescan / Dentsply Sirona)"],
    ["medit", "Medit (i-series)"],
    ["shining3d", "Shining 3D (Aoralscan)"],
    ["other", "Other / no scanner yet"],
];

export default function PracticeRegister() {
    const [sp] = useSearchParams();
    const ref = sp.get("ref") || "";
    const [form, setForm] = useState({
        practice_name: "", contact_name: "", email: "", phone: "",
        country: "", city: "", scanner_brand: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.practice_name.trim() || !form.contact_name.trim() || !form.email.includes("@")) {
            toast.error("Practice name, contact name and a valid email are required");
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/practices/register`, { ...form, ref });
            setResult(res.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Registration failed — try again");
        } finally {
            setSubmitting(false);
        }
    };

    if (result) {
        return (
            <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="practice-register-success">
                <CheckCircle2 size={40} className="text-emerald-500 mx-auto mb-5" />
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-slate-900">
                    {result.already_registered ? "Already registered." : "Request received."}
                </h1>
                <p className="mt-5 text-[15px] text-slate-600 leading-relaxed">
                    {result.already_registered
                        ? "This email is already registered. If you haven't received your portal link yet, the lab is reviewing your request."
                        : "The lab will review your practice and email you a personal portal link — usually within one business day."}
                </p>
                <Link to="/solutions/dental-practice" className="inline-flex items-center gap-2 mt-8 text-[14px] text-cyan-700 hover:text-cyan-500">
                    <ArrowLeft size={14} /> Back to Scan Inbox
                </Link>
            </div>
        );
    }

    return (
        <div className="max-w-lg mx-auto px-6 py-16" data-testid="practice-register-page">
            <div className="text-[11px] font-mono-tr tracking-[0.28em] text-cyan-600 uppercase mb-4">
                Practice registration
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-slate-900">Get your scan portal.</h1>
            <p className="mt-4 text-[15px] text-slate-600">
                Free for practices. Register once — we approve your clinic and email your personal portal link.
            </p>
            <form onSubmit={submit} className="mt-9 space-y-4">
                {ref && (
                    <div className="rounded-xl bg-cyan-50 border border-cyan-200 px-4 py-3 text-[12.5px] text-slate-700" data-testid="pr-referral-note">
                        You were referred by a colleague practice — welcome!
                    </div>
                )}
                <input data-testid="pr-practice-name" value={form.practice_name} onChange={set("practice_name")} placeholder="Practice / clinic name *"
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-cyan-500 outline-none text-[14px]" />
                <div className="grid sm:grid-cols-2 gap-4">
                    <input data-testid="pr-contact-name" value={form.contact_name} onChange={set("contact_name")} placeholder="Contact person *"
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-cyan-500 outline-none text-[14px]" />
                    <input data-testid="pr-phone" value={form.phone} onChange={set("phone")} placeholder="Phone / WhatsApp"
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-cyan-500 outline-none text-[14px]" />
                </div>
                <input data-testid="pr-email" type="email" value={form.email} onChange={set("email")} placeholder="Email *"
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-cyan-500 outline-none text-[14px]" />
                <div className="grid sm:grid-cols-2 gap-4">
                    <input data-testid="pr-country" value={form.country} onChange={set("country")} placeholder="Country"
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-cyan-500 outline-none text-[14px]" />
                    <input data-testid="pr-city" value={form.city} onChange={set("city")} placeholder="City"
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-cyan-500 outline-none text-[14px]" />
                </div>
                <select data-testid="pr-scanner" value={form.scanner_brand} onChange={set("scanner_brand")}
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-cyan-500 outline-none text-[14px] bg-white text-slate-700">
                    <option value="">Which intraoral scanner do you use?</option>
                    {SCANNERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <button data-testid="pr-submit" disabled={submitting}
                    className="w-full py-3.5 rounded-xl bg-slate-900 hover:bg-slate-700 disabled:bg-slate-400 text-white text-[14px] font-medium transition-colors inline-flex items-center justify-center gap-2">
                    {submitting && <Loader2 size={15} className="animate-spin" />}
                    Register practice
                </button>
                <p className="text-[12px] text-slate-400 text-center">No credit card. The lab reviews every registration manually.</p>
            </form>
        </div>
    );
}
